import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Briefcase, Home, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";
import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import Button from "src/components/app/Button";
import Radio from "src/components/common/Radio";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { getAddressListRequest } from "src/redux/slices/locationSlice";
import { benefitActionRequest } from "src/redux/slices/programSlice";
import { AddressListResponse } from "src/types/location.types";
import {
  Milestone,
  MilestoneBenefit,
  MilestoneBenefitAction,
} from "src/types/program.types";
import { constants } from "src/constants/common";
import AddMilestoneAddressBottomSheet from "./AddMilestoneAddressBottomSheet";

const addressTypeIcons = {
  Home: <Home className="w-5 h-5" />,
  Work: <Briefcase className="w-5 h-5" />,
  Other: <MapPin className="w-5 h-5" />,
};

const ClaimMilestoneScreen: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const supportProgramKey = params["support-program-key"];
  const dispatch = useAppDispatch();
  const { milestone, benefit, action } = (location.state || {}) as {
    milestone?: Milestone;
    benefit?: MilestoneBenefit;
    action?: MilestoneBenefitAction;
  };

  const addressList = useAppSelector((state) => state.location.getAddressList);
  const addresses = addressList?.data || [];
  const [selectedAddress, setSelectedAddress] =
    useState<AddressListResponse | null>(null);
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);

  const fetchAddresses = () => {
    new Promise((resolve, reject) => {
      dispatch(getAddressListRequest({ resolve, reject, payload: {} }));
    });
  };

  useEffect(() => {
    fetchAddresses();
  }, [dispatch]);

  useEffect(() => {
    if (addresses.length > 0 && !selectedAddress) {
      setSelectedAddress(addresses[addresses.length - 1]);
    }
  }, [addresses]);

  const handleAddAddressSuccess = () => {
    setIsAddAddressOpen(false);
    // Clear the selection so the auto-select effect above picks the newly
    // added address once the refetched list lands.
    setSelectedAddress(null);
    fetchAddresses();
  };

  const handleClaimNow = () => {
    if (!benefit || !selectedAddress) return;

    setIsClaiming(true);
    // `benefit_name` only exists on the older response shape; the action's
    // params carry whatever identifies the benefit now.
    const payload = {
      ...(benefit.name ? { benefit_name: benefit.name } : {}),
      ...(action?.params || {}),
      addressId: selectedAddress.patient_address_rel_id,
    };

    new Promise((resolve, reject) => {
      dispatch(benefitActionRequest({ resolve, reject, payload }));
    })
      .then(() => {
        toast.success("Successfully Claimed");
        navigate(`/app/${supportProgramKey}/success`, {
          state: {
            success: true,
            successMessageTitle: "Claimed successfully",
            successMessageDescription: `You have successfully claimed ${benefit.title}`,
            redirectUrl: `/app/${supportProgramKey}/program-details`,
          },
        });
      })
      .catch((error) => {
        toast.error(
          error instanceof Error
            ? error.message
            : constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG,
        );
      })
      .finally(() => {
        setIsClaiming(false);
      });
  };

  if (!milestone || !benefit) {
    return null;
  }

  return (
    <Layout
      supportProgramKey={supportProgramKey || ""}
      header={
        <Header
          title="Claim Details"
          titlePosition="center"
          showBackButton={true}
          onBackClick={() => navigate(-1)}
        />
      }
      stickyHeader
    >
      <div className="relative w-full h-full flex flex-col justify-between gap-4">
        <div className="flex flex-col gap-4 p-4">
          <div
            className="w-full rounded-xl p-4 border"
            style={{
              backgroundColor: "var(--icon-bg-primary)",
              borderColor: "var(--app-primary)",
            }}
          >
            <h3
              className="text-base font-bold mb-1"
              style={{ color: "var(--text-primary)" }}
            >
              {benefit.title}
            </h3>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {benefit.description}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-medium text-gray-700">
                Delivery Address
              </h2>
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-medium"
                style={{ color: "var(--app-primary)" }}
                onClick={() => setIsAddAddressOpen(true)}
              >
                <Plus className="w-4 h-4" />
                Add Address
              </button>
            </div>
            {addresses.length === 0 ? (
              <p className="text-sm text-gray-500">
                No saved address found. Add one to proceed.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {addresses.map((address) => {
                  const isSelected =
                    selectedAddress?.patient_address_rel_id ===
                    address.patient_address_rel_id;
                  const icon = addressTypeIcons[
                    address.address_type as keyof typeof addressTypeIcons
                  ] || <MapPin className="w-5 h-5" />;
                  return (
                    <div
                      key={address.patient_address_rel_id}
                      className="flex items-start gap-3 p-3 bg-white rounded-xl border cursor-pointer"
                      style={{
                        borderColor: isSelected
                          ? "var(--app-primary)"
                          : "var(--card-border)",
                      }}
                      onClick={() => setSelectedAddress(address)}
                    >
                      <div className="pt-0.5 text-gray-500">
                        {React.cloneElement(icon, {
                          className: "w-5 h-5",
                          style: {
                            color: isSelected
                              ? "var(--app-primary)"
                              : undefined,
                          },
                        })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm ${
                            isSelected
                              ? "text-[var(--app-primary)] font-bold"
                              : "text-[var(--text-primary)] font-medium"
                          }`}
                        >
                          {address.address_type}
                        </p>
                        <p className="text-xs text-gray-500">
                          {address.address}
                        </p>
                      </div>
                      <Radio
                        checked={isSelected}
                        onChange={() => setSelectedAddress(address)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="relative w-full bg-white border-t border-gray-200 p-4">
          <Button
            rounded="full"
            fullWidth
            disabled={!selectedAddress || isClaiming}
            loading={isClaiming}
            onClick={handleClaimNow}
          >
            {action?.label || benefit.buttonLabel || "Claim Now"}
          </Button>
        </div>
      </div>

      <AddMilestoneAddressBottomSheet
        show={isAddAddressOpen}
        onClose={() => setIsAddAddressOpen(false)}
        onSuccess={handleAddAddressSuccess}
      />
    </Layout>
  );
};

export default ClaimMilestoneScreen;
